(() => {
  'use strict';

  const API_KEY = Symbol.for('totoka.routine-builder-master');
  if (window[API_KEY]) {
    window[API_KEY].init(document);
    return;
  }

  const SELECTOR = '[data-totoka-routine-builder]';
  const instances = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (value) => {
    const x = clamp(value);
    return x * x * (3 - (2 * x));
  };

  class TotokaRoutineBuilder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || 'default';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduceMotion = root.dataset.disableAnimations === 'true' || this.motionQuery.matches;
      this.hasBuilt = false;
      this.isAdding = false;
      const lang = document.documentElement.lang.toLowerCase().split('-')[0];
      this.copy = ({
        it: {choose:'Seleziona almeno un obiettivo per continuare.', empty:'Non ci sono prodotti disponibili per queste scelte. Contattaci per trovare il prossimo passo.', contact:'Contattaci', save:'Salva le mie preferenze', saved:'Preferenze salvate su questo dispositivo.', error:'Non possiamo confermare l’aggiunta. Controlla il carrello prima di riprovare.', cart:'Vai al carrello'},
        de: {choose:'Wähle mindestens ein Hautziel aus.', empty:'Für diese Auswahl sind derzeit keine Produkte verfügbar. Kontaktiere uns für Unterstützung.', contact:'Kontakt aufnehmen', save:'Meine Auswahl speichern', saved:'Deine Auswahl wurde auf diesem Gerät gespeichert.', error:'Das Hinzufügen konnte nicht bestätigt werden. Bitte prüfe deinen Warenkorb, bevor du es erneut versuchst.', cart:'Zum Warenkorb'},
        en: {choose:'Select at least one skin goal to continue.', empty:'No products are available for these choices yet. Contact us for help with your next step.', contact:'Contact us', save:'Save my preferences', saved:'Preferences saved on this device.', error:'We could not confirm the addition. Please check your cart before trying again.', cart:'View cart'}
      })[lang] || {choose:'Select at least one skin goal to continue.', empty:'No products are available for these choices yet. Contact us for help with your next step.', contact:'Contact us', save:'Save my preferences', saved:'Preferences saved on this device.', error:'We could not confirm the addition. Please check your cart before trying again.', cart:'View cart'};
      this.structureCopy = ({
        it: {nav:'Costruisci la tua routine', goals:'Obiettivi', preferences:'Preferenze', routine:'La tua routine', edit:'Modifica le scelte', back:'Modifica gli obiettivi', reset:'Ricomincia', selected:'Obiettivi selezionati', sensitivity:'Sensibilità'},
        de: {nav:'Stelle deine Routine zusammen', goals:'Hautziele', preferences:'Auswahl', routine:'Deine Routine', edit:'Auswahl ändern', back:'Hautziele ändern', reset:'Neu beginnen', selected:'Gewählte Hautziele', sensitivity:'Empfindlichkeit'},
        en: {nav:'Build your routine', goals:'Skin goals', preferences:'Preferences', routine:'Your routine', edit:'Edit my choices', back:'Edit skin goals', reset:'Start over', selected:'Selected skin goals', sensitivity:'Sensitivity'}
      })[lang] || {nav:'Build your routine', goals:'Skin goals', preferences:'Preferences', routine:'Your routine', edit:'Edit my choices', back:'Edit skin goals', reset:'Start over', selected:'Selected skin goals', sensitivity:'Sensitivity'};
      this.structureNodes = [];
      this.goals = [];
      this.calibration = {
        skinType: root.dataset.defaultSkinType || 'normal',
        sensitivity: root.dataset.defaultSensitivity || 'moderate',
        time: root.dataset.defaultTime || 'both',
        depth: root.dataset.defaultDepth || 'advanced'
      };
      this.activeSteps = [];
      this.ticking = false;
      this.isSectionVisible = true;
      this.destroyed = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.goalButtons = [...root.querySelectorAll('[data-trb-goal]')];
      this.steps = [...root.querySelectorAll('[data-trb-step]')];
      this.routineScene = root.querySelector('[data-trb-scene="routine"]');
      this.summaryScene = root.querySelector('[data-trb-scene="summary"]');
      this.liveStatus = root.querySelector('[data-trb-live-status]');
      this.storageKey = `totokaRoutineBuilder:${this.sectionId}`;

      this.root.classList.add('totoka-routine-builder-master--js');
      this.setupEmptyState();
      this.setupStructure();
      const saveControl = this.root.querySelector('[data-trb-save]');
      if (saveControl) saveControl.textContent = this.copy.save;
      this.routineScene && (this.routineScene.hidden = true);
      this.summaryScene && (this.summaryScene.hidden = true);
      this.steps.forEach(step => { step.hidden = true; });
      this.bindEvents();
      this.setupObservers();
      this.restoreState();
      this.updateGoalUI();
      this.updateSummary();
      this.syncActions();
      this.updateVideoState();
      this.scheduleFrame();
    }

    bindEvents() {
      this.goalButtons.forEach((button) => {
        button.addEventListener('click', () => this.toggleGoal(button), { signal: this.signal });
      });

      this.root.querySelectorAll('[data-trb-go-to]').forEach((control) => {
        control.addEventListener('click', () => this.goToScene(control.dataset.trbGoTo), { signal: this.signal });
      });

      const form = this.root.querySelector('[data-trb-console]');
      if (form) {
        form.addEventListener('change', () => {
          if (this.isAdding) return;
          this.readCalibration();
          if (this.hasBuilt) this.buildRoutine();
          else this.updateSummary();
        }, { signal: this.signal });
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.readCalibration();
          if (this.buildRoutine()) this.goToScene('routine');
        }, { signal: this.signal });
      }

      const addAll = this.root.querySelector('[data-trb-add-all]');
      if (addAll) addAll.addEventListener('click', () => this.addAllToCart(addAll), { signal: this.signal });

      const save = this.root.querySelector('[data-trb-save]');
      if (save) save.addEventListener('click', () => this.saveState(), { signal: this.signal });

      this.root.querySelectorAll('[data-trb-reset]').forEach(reset => {
        reset.addEventListener('click', () => this.reset(), { signal: this.signal });
      });

      this.motionQuery.addEventListener('change', () => {
        this.reduceMotion = this.root.dataset.disableAnimations === 'true' || this.motionQuery.matches;
        this.updateVideoState();
        this.scheduleFrame();
      }, { signal: this.signal });
      document.addEventListener('visibilitychange', () => this.updateVideoState(), { signal: this.signal });
      window.addEventListener('scroll', () => this.scheduleFrame(), { passive: true, signal: this.signal });
      window.addEventListener('resize', () => this.scheduleFrame(), { passive: true, signal: this.signal });
    }

    setupObservers() {
      if (!('IntersectionObserver' in window)) {
        this.root.querySelectorAll('[data-trb-reveal]').forEach((item) => item.classList.add('is-visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });

      this.root.querySelectorAll('[data-trb-reveal]').forEach((item) => this.revealObserver.observe(item));

      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isSectionVisible = entry.isIntersecting;
          this.updateVideoState();
          if (entry.isIntersecting) this.scheduleFrame();
          else this.updateScrollState();
        });
      }, { rootMargin: '20% 0px 20% 0px', threshold: 0 });
      this.visibilityObserver.observe(this.root);

      this.sceneObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('is-active', entry.isIntersecting));
      }, { rootMargin: '-34% 0px -34% 0px', threshold: 0 });

      this.root.querySelectorAll('[data-trb-scene]').forEach((scene) => this.sceneObserver.observe(scene));

      this.stepObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.hidden) entry.target.classList.add('is-step-visible');
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });

      this.steps.forEach((step) => this.stepObserver.observe(step));

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleFrame());
        this.resizeObserver.observe(this.root);
      }
    }

    toggleGoal(button) {
      if (this.isAdding) return;
      const key = button.dataset.goalKey;
      const existing = this.goals.indexOf(key);

      if (existing !== -1) {
        this.goals.splice(existing, 1);
      } else if (this.goals.length < 2) {
        this.goals.push(key);
      } else {
        this.goals.splice(1, 1, key);
      }

      this.updateGoalUI();
      if (this.hasBuilt) this.buildRoutine();
      else this.updateSummary();
      this.syncActions();
    }

    updateGoalUI() {
      this.goalButtons.forEach((button) => {
        const index = this.goals.indexOf(button.dataset.goalKey);
        button.classList.toggle('is-primary', index === 0);
        button.classList.toggle('is-secondary', index === 1);
        button.setAttribute('aria-pressed', index !== -1 ? 'true' : 'false');
      });

      this.root.querySelectorAll('[data-trb-mini-goal], [data-trb-summary-goal]').forEach((node) => {
        const key = node.dataset.trbMiniGoal || node.dataset.trbSummaryGoal;
        const index = this.goals.indexOf(key);
        node.classList.toggle('is-primary', index === 0);
        node.classList.toggle('is-secondary', index === 1);
      });

      const status = this.root.querySelector('[data-trb-goal-status]');
      const next = this.root.querySelector('[data-trb-goal-next]');
      if (next) next.disabled = this.goals.length === 0;

      if (status) {
        if (!this.goals.length) {
          status.textContent = this.root.dataset.emptyGoalMessage || 'Select up to two goals';
        } else {
          const labels = this.goals.map((goal) => this.goalLabel(goal));
          const primaryLabel = this.root.dataset.primaryLabel || 'Primary';
          const secondaryLabel = this.root.dataset.secondaryLabel || 'Secondary';
          status.textContent = labels.length === 1
            ? `${primaryLabel}: ${labels[0]}`
            : `${primaryLabel}: ${labels[0]} · ${secondaryLabel}: ${labels[1]}`;
        }
      }
    }

    setupStructure() {
      const copy = this.structureCopy;
      const goalsScene = this.root.querySelector('[data-trb-scene="goals"]');
      const calibrationScene = this.root.querySelector('[data-trb-scene="calibration"]');
      const makeButton = (label, target) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'totoka-routine-builder-master__text-button trb-structure-link';
        button.textContent = label;
        if (target) button.dataset.trbGoTo = target;
        return button;
      };
      if (goalsScene) {
        const nav = document.createElement('nav');
        nav.className = 'trb-progress';
        nav.setAttribute('aria-label', copy.nav);
        const list = document.createElement('ol');
        [['goals',copy.goals],['calibration',copy.preferences],['routine',copy.routine]].forEach(([target,label], index) => {
          if (!this.root.querySelector(`[data-trb-scene="${target}"]`)) return;
          const item = document.createElement('li');
          const button = makeButton(label, target);
          button.dataset.trbProgress = target;
          const number = document.createElement('span');
          number.textContent = String(index + 1).padStart(2, '0');
          number.setAttribute('aria-hidden','true');
          button.prepend(number);
          item.append(button);
          list.append(item);
        });
        nav.append(list);
        goalsScene.prepend(nav);
        this.structureNodes.push(nav);
      }
      if (calibrationScene) {
        const form = calibrationScene.querySelector('[data-trb-console]');
        if (form) {
          const context = document.createElement('div');
          context.className = 'trb-selected-context';
          context.hidden = true;
          const label = document.createElement('span');
          label.className = 'trb-selected-label';
          label.textContent = copy.selected;
          this.selectedGoalsText = document.createElement('p');
          context.append(label, this.selectedGoalsText);
          if (goalsScene) context.append(makeButton(copy.back, 'goals'));
          form.prepend(context);
          this.selectedGoalsContext = context;
          this.structureNodes.push(context);
        }
      }
      if (this.routineScene) {
        const actions = document.createElement('div');
        actions.className = 'trb-result-actions';
        if (calibrationScene) actions.append(makeButton(copy.edit, 'calibration'));
        else if (goalsScene) actions.append(makeButton(copy.back, 'goals'));
        const reset = makeButton(copy.reset);
        reset.dataset.trbReset = '';
        actions.append(reset);
        this.routineScene.querySelector('.totoka-routine-builder-master__routine-footer')?.append(actions);
        this.structureNodes.push(actions);
      }
      const summaryList = this.summaryScene?.querySelector('.totoka-routine-builder-master__summary-list');
      if (summaryList && !summaryList.querySelector('[data-trb-summary-value="sensitivity"]')) {
        const row = document.createElement('div');
        const label = document.createElement('dt');
        label.textContent = this.root.querySelector('input[name^="sensitivity-"]')?.closest('fieldset')?.querySelector('legend')?.textContent || copy.sensitivity;
        const value = document.createElement('dd');
        value.dataset.trbSummaryValue = 'sensitivity';
        row.append(label, value);
        summaryList.querySelector('[data-trb-summary-value="skinType"]')?.parentElement.after(row);
        this.structureNodes.push(row);
      }
    }

    updateStructure() {
      this.root.querySelectorAll('[data-trb-progress]').forEach(button => {
        button.disabled = this.isAdding;
      });
      if (this.selectedGoalsContext) this.selectedGoalsContext.hidden = !this.goals.length;
      if (this.selectedGoalsText) this.selectedGoalsText.textContent = this.goals.map(key => this.goalLabel(key)).join(' · ');
      const secondary = this.root.querySelector('[data-trb-summary-value="secondary"]');
      if (secondary) secondary.parentElement.hidden = !this.goals[1];
    }

    goalLabel(key) {
      const match = this.goalButtons.find((button) => button.dataset.goalKey === key);
      return match?.dataset.goalLabel || key;
    }

    goToScene(name) {
      if (this.isAdding) return;
      const sceneOrder = ['hero', 'goals', 'calibration', 'routine', 'summary'];
      const requestedIndex = Math.max(0, sceneOrder.indexOf(name));
      let scene = this.root.querySelector(`[data-trb-scene="${CSS.escape(name)}"]`);
      if (!scene) {
        for (let index = requestedIndex + 1; index < sceneOrder.length; index += 1) {
          scene = this.root.querySelector(`[data-trb-scene="${sceneOrder[index]}"]`);
          if (scene) break;
        }
      }
      if (!scene) return;
      if (['routine', 'summary'].includes(scene.dataset.trbScene) && !this.goals.length) {
        const goalsScene = this.root.querySelector('[data-trb-scene="goals"]');
        goalsScene?.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
        this.requireGoal();
        return;
      }
      if (['routine', 'summary'].includes(scene.dataset.trbScene) && !this.hasBuilt) {
        this.readCalibration();
        if (!this.buildRoutine()) return;
      }
      if (scene.hidden) return;
      scene.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
      const heading = scene.querySelector('h1,h2');
      if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    }

    readCalibration() {
      const form = this.root.querySelector('[data-trb-console]');
      if (!form) return this.calibration;

      const read = (prefix, fallback) => form.querySelector(`input[name^="${prefix}"]:checked`)?.value || fallback;
      this.calibration = {
        skinType: read('skin-type-', this.root.dataset.defaultSkinType || 'normal'),
        sensitivity: read('sensitivity-', this.root.dataset.defaultSensitivity || 'moderate'),
        time: read('routine-time-', this.root.dataset.defaultTime || 'both'),
        depth: read('routine-depth-', this.root.dataset.defaultDepth || 'advanced')
      };
      return this.calibration;
    }

    parseList(value) {
      return String(value || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
    }

    stepMatch(step) {
      if (step.dataset.enabled !== 'true' || !/^[1-9]\d*$/.test(step.dataset.variantId || '')) return null;

      const goals = this.parseList(step.dataset.goals);
      const skinTypes = this.parseList(step.dataset.skinTypes);
      const selectedSensitivity = { low: 1, moderate: 2, high: 3 }[this.calibration.sensitivity] || 2;
      const maximumSensitivity = { low: 1, moderate: 2, high: 3 }[step.dataset.sensitivityMax] || 3;
      const selectedDepth = { essential: 1, advanced: 2, full: 3 }[this.calibration.depth] || 2;
      const minimumDepth = { essential: 1, advanced: 2, full: 3 }[step.dataset.minDepth] || 1;
      const time = step.dataset.time || 'both';
      const timeMatches = this.calibration.time === 'both' || time === 'both' || time === this.calibration.time;
      const goalMatches = !goals.length || goals.some((goal) => this.goals.includes(goal));
      const skinMatches = !skinTypes.length || this.calibration.skinType === 'unsure' || skinTypes.includes(this.calibration.skinType);

      if (!timeMatches || !goalMatches || !skinMatches || selectedSensitivity > maximumSensitivity || selectedDepth < minimumDepth) return null;

      let score = 0;
      if (goals.includes(this.goals[0])) score += 8;
      if (goals.includes(this.goals[1])) score += 5;
      if (skinTypes.includes(this.calibration.skinType)) score += 3;
      if (time === this.calibration.time) score += 2;
      if (!goals.length) score += 1;

      return {
        element: step,
        key: step.dataset.stepKey || '',
        order: Number(step.dataset.order) || 99,
        score
      };
    }

    buildRoutine() {
      if (this.isAdding) return false;
      if (!this.goals.length) {
        this.hasBuilt = false;
        this.activeSteps = [];
        this.steps.forEach(step => { step.hidden = true; });
        if (this.routineScene) this.routineScene.hidden = true;
        if (this.summaryScene) this.summaryScene.hidden = true;
        this.updateSummary();
        this.syncActions();
        this.requireGoal();
        return false;
      }
      this.hasBuilt = true;
      if (this.routineScene) this.routineScene.hidden = false;
      if (this.summaryScene) this.summaryScene.hidden = false;
      this.setLiveStatus('');
      const candidates = this.steps.map((step) => this.stepMatch(step)).filter(Boolean);
      const bestByKey = new Map();

      candidates.forEach((candidate) => {
        const current = bestByKey.get(candidate.key);
        if (!current || candidate.score > current.score || (candidate.score === current.score && candidate.order < current.order)) {
          bestByKey.set(candidate.key, candidate);
        }
      });

      const limits = { essential: 4, advanced: 7, full: 10 };
      this.activeSteps = [...bestByKey.values()]
        .sort((a, b) => a.order - b.order)
        .slice(0, limits[this.calibration.depth] || 7);

      const activeElements = new Set(this.activeSteps.map((item) => item.element));
      this.steps.forEach((step) => {
        const active = activeElements.has(step);
        step.hidden = !active;
        step.classList.remove('is-step-visible');
        if (active) {
          const position = this.activeSteps.findIndex((item) => item.element === step);
          step.style.order = position;
        }
      });

      requestAnimationFrame(() => {
        this.activeSteps.forEach(({ element }) => {
          const rect = element.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.92) element.classList.add('is-step-visible');
        });
      });

      const count = this.activeSteps.length;
      const countNode = this.root.querySelector('[data-trb-routine-count]');
      if (countNode) {
        countNode.textContent = `${count} ${this.root.dataset.stepsLabel || 'recommended steps'}`;
        countNode.hidden = count === 0;
      }

      if (this.emptyState) this.emptyState.hidden = count > 0;
      // An empty match is not presented as a completed routine.
      if (this.summaryScene) this.summaryScene.hidden = count === 0;
      this.root.querySelectorAll('[data-trb-go-to="summary"]').forEach(control => { control.hidden = count === 0; });
      this.syncActions();

      this.updateSummary();
      this.scheduleFrame();
      return true;
    }

    optionLabel(prefix, value) {
      const input = this.root.querySelector(`input[name^="${prefix}"][value="${CSS.escape(value)}"]`);
      return input?.nextElementSibling?.textContent?.trim() || value;
    }

    updateSummary() {
      const values = {
        primary: this.goals[0] ? this.goalLabel(this.goals[0]) : '—',
        secondary: this.goals[1] ? this.goalLabel(this.goals[1]) : '—',
        skinType: this.optionLabel('skin-type-', this.calibration.skinType),
        sensitivity: this.optionLabel('sensitivity-', this.calibration.sensitivity),
        time: this.optionLabel('routine-time-', this.calibration.time),
        depth: this.optionLabel('routine-depth-', this.calibration.depth),
        steps: String(this.activeSteps.length)
      };

      Object.entries(values).forEach(([key, value]) => {
        const node = this.root.querySelector(`[data-trb-summary-value="${key}"]`);
        if (node) node.textContent = value;
      });
    }

    saveState() {
      if (this.isAdding || !this.goals.length) return;
      this.readCalibration();
      const payload = {
        goals: this.goals,
        calibration: this.calibration,
        savedAt: new Date().toISOString()
      };

      try {
        localStorage.setItem(this.storageKey, JSON.stringify(payload));
        this.setLiveStatus(this.copy.saved);
        this.root.dispatchEvent(new CustomEvent('totoka:routine:saved', { bubbles: true, detail: payload }));
      } catch (error) {
        this.setLiveStatus(this.root.dataset.saveErrorMessage || 'The routine could not be saved.');
      }
    }

    restoreState() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
        if (!stored) return;
        const validGoals = this.goalButtons.map((button) => button.dataset.goalKey);
        this.goals = Array.isArray(stored.goals) ? [...new Set(stored.goals.filter((goal) => validGoals.includes(goal)))].slice(0, 2) : [];
        const fields = {skinType:'skin-type-', sensitivity:'sensitivity-', time:'routine-time-', depth:'routine-depth-'};
        Object.entries(fields).forEach(([key, prefix]) => {
          const value = stored.calibration?.[key];
          if (typeof value === 'string' && this.root.querySelector(`input[name^="${prefix}"][value="${CSS.escape(value)}"]`)) this.calibration[key] = value;
        });

        const inputMap = {
          'skin-type-': this.calibration.skinType,
          'sensitivity-': this.calibration.sensitivity,
          'routine-time-': this.calibration.time,
          'routine-depth-': this.calibration.depth
        };
        Object.entries(inputMap).forEach(([prefix, value]) => {
          const input = this.root.querySelector(`input[name^="${prefix}"][value="${CSS.escape(value)}"]`);
          if (input) input.checked = true;
        });
        if (this.goals.length) this.buildRoutine();
      } catch (error) {
        try { localStorage.removeItem(this.storageKey); } catch (_) { /* Unavailable storage must not block the builder. */ }
      }
    }

    async addAllToCart(button) {
      if (this.isAdding || button.disabled) return;
      this.readCalibration();
      if (!this.buildRoutine()) return;
      const quantities = new Map();
      this.activeSteps.forEach(({ element }) => {
        const id = Number(element.dataset.variantId);
        if (id) quantities.set(id, (quantities.get(id) || 0) + 1);
      });
      const items = [...quantities].map(([id, quantity]) => ({ id, quantity }));

      if (!items.length) {
        this.setLiveStatus(this.copy.empty);
        return;
      }

      this.isAdding = true;
      const locked = [...this.root.querySelectorAll('button,input')].map(control => [control, control.disabled]);
      locked.forEach(([control]) => { control.disabled = true; });
      button.setAttribute('aria-busy', 'true');
      try {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        const cartData = await response.json().catch(() => null);
        this.setLiveStatus(this.root.dataset.addedMessage || 'Routine added to cart.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this.root.dispatchEvent(new CustomEvent('totoka:routine:added', { bubbles: true, detail: { items, cartData } }));
        if (!this.destroyed) window.location.assign(`${shopRoot}cart`);
      } catch (error) {
        this.setLiveStatus(this.copy.error);
        if (this.liveStatus) {
          const link = document.createElement('a');
          link.href = `${window.Shopify?.routes?.root || '/'}cart`;
          link.textContent = this.copy.cart;
          link.className = 'totoka-routine-builder-master__text-link';
          this.liveStatus.append(document.createTextNode(' '), link);
        }
      } finally {
        this.isAdding = false;
        locked.forEach(([control, disabled]) => { control.disabled = disabled; });
        button.removeAttribute('aria-busy');
        this.syncActions();
      }
    }

    setLiveStatus(message) {
      if (this.liveStatus) this.liveStatus.textContent = message;
    }

    reset() {
      if (this.isAdding) return;
      this.hasBuilt = false;
      if (this.routineScene) this.routineScene.hidden = true;
      if (this.summaryScene) this.summaryScene.hidden = true;
      this.goals = [];
      this.calibration = {
        skinType: this.root.dataset.defaultSkinType || 'normal',
        sensitivity: this.root.dataset.defaultSensitivity || 'moderate',
        time: this.root.dataset.defaultTime || 'both',
        depth: this.root.dataset.defaultDepth || 'advanced'
      };
      this.activeSteps = [];
      try { localStorage.removeItem(this.storageKey); } catch (error) { /* Storage may be unavailable. */ }

      const inputMap = {
        'skin-type-': this.calibration.skinType,
        'sensitivity-': this.calibration.sensitivity,
        'routine-time-': this.calibration.time,
        'routine-depth-': this.calibration.depth
      };
      Object.entries(inputMap).forEach(([prefix, value]) => {
        const input = this.root.querySelector(`input[name^="${prefix}"][value="${CSS.escape(value)}"]`);
        if (input) input.checked = true;
      });
      this.steps.forEach((step) => {
        step.hidden = true;
        step.classList.remove('is-step-visible');
      });
      this.setLiveStatus('');
      this.updateGoalUI();
      this.updateSummary();
      this.syncActions();
      this.goToScene(this.root.querySelector('[data-trb-scene="goals"]') ? 'goals' : 'hero');
    }

    syncActions() {
      this.updateStructure();
      const add = this.root.querySelector('[data-trb-add-all]');
      const save = this.root.querySelector('[data-trb-save]');
      if (add) add.disabled = this.isAdding || !this.activeSteps.length;
      if (save) save.disabled = this.isAdding || !this.goals.length;
    }

    requireGoal() {
      const status = this.root.querySelector('[data-trb-goal-status]');
      if (status) status.textContent = this.copy.choose;
      this.goalButtons[0]?.focus({ preventScroll: true });
    }

    setupEmptyState() {
      if (!this.routineScene) return;
      this.emptyState = document.createElement('div');
      this.emptyState.className = 'trb-empty';
      this.emptyState.hidden = true;
      this.emptyState.setAttribute('role', 'status');
      const text = document.createElement('p');
      text.textContent = this.copy.empty;
      const link = document.createElement('a');
      link.href = `${window.Shopify?.routes?.root || '/'}pages/contact`;
      link.textContent = this.copy.contact;
      this.emptyState.append(text, link);
      this.routineScene.querySelector('[data-trb-spine]')?.prepend(this.emptyState);
    }

    updateVideoState() {
      const play = this.isSectionVisible && !this.reduceMotion && !document.hidden && !this.destroyed;
      this.root.querySelectorAll('video').forEach(video => {
        video.autoplay = play;
        if (play) video.play()?.catch(() => {});
        else video.pause();
      });
    }

    scheduleFrame() {
      if (this.ticking || this.destroyed || !this.isSectionVisible) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateScrollState();
      });
    }

    updateScrollState() {
      if (!this.routineScene || this.reduceMotion) return;
      const rect = this.routineScene.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const fadeDistance = viewportHeight * 0.72;
      const enter = smoothstep((viewportHeight - rect.top) / fadeDistance);
      const exit = smoothstep(rect.bottom / fadeDistance);
      const deepProgress = Math.min(enter, exit);
      const spineTravel = Math.max(1, rect.height - (viewportHeight * 0.45));
      const spineProgress = clamp(((viewportHeight * 0.76) - rect.top) / spineTravel);
      this.root.style.setProperty('--trb-deep-progress', deepProgress.toFixed(4));
      this.root.style.setProperty('--trb-spine-progress', spineProgress.toFixed(4));
    }

    destroy() {
      this.destroyed = true;
      this.updateVideoState();
      this.emptyState?.remove();
      this.structureNodes.forEach(node => node.remove());
      this.abortController.abort();
      this.revealObserver?.disconnect();
      this.sceneObserver?.disconnect();
      this.stepObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.root.classList.remove('totoka-routine-builder-master--js');
      instances.delete(this.root);
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new TotokaRoutineBuilder(root));
    });
  };

  const destroy = (scope) => {
    if (scope.matches?.(SELECTOR)) instances.get(scope)?.destroy();
    scope.querySelectorAll?.(SELECTOR).forEach((root) => instances.get(root)?.destroy());
  };

  window[API_KEY] = { init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(SELECTOR);
    if (!root) return;
    const scene = event.target.closest?.('[data-trb-scene]');
    if (scene) scene.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();
